import { PeriodBehavior } from '../../../components/DatePicker/AvDateRangePicker.constants';
import { Filter } from '../../../types/filter.types';
import { field } from '../filter-builder';
import {
  cleanLikeValue,
  isValidContainsValue,
  isValidEndWithValue,
  isValidNotContainsValue,
  isValidStartWithValue,
  NullOperator,
  NumberOperator,
  StringOperator,
  SupportedFunction,
} from './SqlToFilterConverter.utils';

type ScalarHandler<T> = (b: ReturnType<typeof field>, v: T) => Filter;
type ArrayHandler<T> = (b: ReturnType<typeof field>, v: T[]) => Filter;

function handlers<T>(
  base: ReturnType<typeof field>,
  op: string,
  value: T | T[],
  scalar: Record<string, ScalarHandler<T>>,
  array: Record<string, ArrayHandler<T>> = {}
) {
  if (Array.isArray(value)) {
    const fn = array[op];
    if (!fn) {
      throw new Error(`Operator '${op}' requires single value, not array`);
    }
    return fn(base, value);
  }
  const fn = scalar[op];
  if (!fn) {
    throw new Error(`Operator '${op}' requires array values.`);
  }
  return fn(base, value);
}

const numScalar: Record<Exclude<NumberOperator, 'BETWEEN'>, ScalarHandler<number>> = {
  '=': (b, v) => b.num.equals(v),
  '!=': (b, v) => b.num.notEqual(v),
  '>': (b, v) => b.num.gt(v),
  '<': (b, v) => b.num.lt(v),
  '>=': (b, v) => b.num.gte(v),
  '<=': (b, v) => b.num.lte(v),
};

const numArray: Record<Exclude<NumberOperator, keyof typeof numScalar>, ArrayHandler<number>> = {
  BETWEEN: (b, [from, to]) => b.num.between(from, to),
};

const strScalar: Record<Exclude<StringOperator, 'IN' | 'NOT IN'>, ScalarHandler<string>> = {
  '=': (b, v) => b.str.equals(v),
  '!=': (b, v) => b.str.notEqual(v),
  LIKE: (b, v) => {
    if (isValidContainsValue(v)) {
      return b.str.contains(cleanLikeValue(v));
    }

    // TODO: Fix starts-with/ends-with conflict when empty string @nizans
    if (isValidStartWithValue(v)) {
      return b.str.starts(cleanLikeValue(v));
    }
    // TODO: Fix starts-with/ends-with conflict when empty string @nizans
    if (isValidEndWithValue(v)) {
      return b.str.ends(cleanLikeValue(v));
    }

    throw new Error(`Invalid LIKE pattern '${v}'. Use: 'text%' (starts), '%text' (ends), or '%text%' (contains)`);
  },
  'NOT LIKE': (b, v) => {
    if (isValidNotContainsValue(v)) {
      return b.str.notContains(cleanLikeValue(v));
    }
    throw new Error(`NOT LIKE only supports contains pattern. Use '%text%', got '${v}'`);
  },
};

const strArray: Record<Exclude<StringOperator, keyof typeof strScalar>, ArrayHandler<string>> = {
  IN: (b, vals) => b.str.in(vals),
  'NOT IN': (b, vals) => b.str.notIn(vals),
};

const boolScalar: Record<string, ScalarHandler<boolean>> = {
  '=': (b, v) => b.bool.equals(v),
};

const dateScalar: Record<string, ScalarHandler<string>> = {
  '=': (b, v) => b.date.equal(v),
  '!=': (b, v) => b.date.notEqual(v),
};

const dateArray: Record<string, ArrayHandler<string>> = {
  BETWEEN: (b, [f, t]) => b.date.between(new Date(f), new Date(t)),
};

const number = (fieldName: string, op: string, value: number | number[]) => handlers(field(fieldName), op, value, numScalar, numArray);
const string = (fieldName: string, op: string, value: string | string[]) => handlers(field(fieldName), op, value, strScalar, strArray);
const boolean = (fieldName: string, op: string, value: boolean) => handlers(field(fieldName), op, value, boolScalar);
const date = (fieldName: string, op: string, value: string | string[]) => handlers(field(fieldName), op, value, dateScalar, dateArray);

export const conditionBuilderFactory = (columnType: 'date' | 'string' | 'boolean' | 'number') => {
  const handler = (
    {
      date,
      string,
      boolean,
      number,
    } as const satisfies Record<typeof columnType, unknown>
  )[columnType];
  if (handler) {
    return handler;
  }

  handler satisfies never;
  throw new Error(`Unsupported field type: ${columnType}`);
};

export type NullableField = 'string' | 'boolean' | 'date' | 'number';
export const makeNullCondition = (fieldName: string, fieldType: NullableField, op: NullOperator) => {
  const empty = op === 'IS';
  const method = empty ? 'empty' : 'notEmpty';
  const builder = field(fieldName);

  const handler = (
    {
      boolean: builder.bool[method],
      date: builder.date[method],
      number: builder.num[method],
      string: builder.str[method],
    } as const satisfies Record<NullableField, unknown>
  )[fieldType];

  if (handler) {
    return handler();
  }

  handler satisfies never;
  throw new Error(`Invalid NULL operator '${op}' for ${fieldType} field. Use: IS NULL or IS NOT NULL`);
};

type BuildRelativeDateCondition = (fieldName: string, args: [PeriodBehavior, number, string]) => Filter;
const buildRelativeDateCondition: BuildRelativeDateCondition = (fieldName, [periodBehavior, value, unit]) =>
  field(fieldName).date.relative(periodBehavior, value, unit);

export const functionConditionBuilder = (fieldName: string, functionName: SupportedFunction, args: unknown[]) => {
  if (functionName === 'RELATIVE_DATE') {
    // @ts-expect-error: fix types @nizans
    return buildRelativeDateCondition(fieldName, args);
  }

  throw new Error(`Unsupported function '${functionName}'`);
};
